import { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE, getProductionId } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Wallet, ArrowUpRight, ArrowDownRight, Download, Trash2, Pencil, Receipt, Tag, Filter, PieChart } from "lucide-react";
import DatePickerField from "@/components/DatePickerField";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDate } from "@/lib/format";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CAT_COLORS = ["#F59E0B","#EF4444","#10B981","#3B82F6","#A78BFA","#EC4899","#06B6D4","#84CC16","#F97316","#8B5CF6"];

export default function Budget() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const canEdit = ["org_admin", "producer", "production_staff", "super_admin"].includes(user?.role);
  const productionId = getProductionId();

  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [cats, setCats] = useState([]);
  const [filters, setFilters] = useState({ category_id: "", type: "", start_date: "", end_date: "" });
  const [txDialog, setTxDialog] = useState(null); // { mode: "new"|"edit", tx?: {...} }
  const [catDialog, setCatDialog] = useState(false);

  const load = async () => {
    const params = { production_id: productionId };
    const [s, t, c] = await Promise.all([
      api.get("/budget/summary", { params }),
      api.get("/budget/transactions", { params: { ...params, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) } }),
      api.get("/budget/categories", { params }),
    ]);
    setSummary(s.data); setTxs(t.data); setCats(c.data);
  };
  useEffect(() => { load(); }, [filters]);

  const del = async (tx) => {
    const ok = await confirm({ title: `Delete this ${tx.type}?`, body: `${usd(tx.amount)} will be removed from the ledger.`, confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    await api.delete(`/budget/transactions/${tx.id}`);
    toast.success("Deleted"); load();
  };

  const runningTotals = useMemo(() => {
    // build running balance from oldest → newest, then flip for display
    const sorted = [...txs].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : (a.created_at < b.created_at ? -1 : 1)));
    let bal = 0;
    const map = {};
    for (const t of sorted) {
      bal += t.type === "income" ? Number(t.amount) : -Number(t.amount);
      map[t.id] = bal;
    }
    return map;
  }, [txs]);

  const exportCsv = async () => {
    const token = localStorage.getItem("sc_token");
    const res = await fetch(`${API_BASE}/budget/export.csv?production_id=${productionId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (!res.ok) return toast.error("Export failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `budget-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (!summary) return <div className="text-zinc-500 text-sm">Loading budget…</div>;

  return (
    <div className="space-y-6" data-testid="budget-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-1">Finance</div>
          <h1 className="font-display text-4xl sm:text-5xl">Budget Tracker</h1>
          <p className="text-zinc-500 text-sm mt-2">Checkbook-style ledger for the production. Every entry has a running balance.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button data-testid="manage-categories-btn" onClick={() => setCatDialog(true)} className="h-10 px-3.5 rounded border border-white/15 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center gap-1.5"><Tag className="w-4 h-4" /> Categories</button>
            <button data-testid="export-csv-btn" onClick={exportCsv} className="h-10 px-3.5 rounded border border-white/15 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center gap-1.5"><Download className="w-4 h-4" /> Export CSV</button>
            <button data-testid="new-tx-btn" onClick={() => setTxDialog({ mode: "new" })} style={goldBtn} className="h-10 px-4 rounded font-semibold text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add entry</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Balance" value={usd(summary.balance)} sub={`${summary.transaction_count} entries`} tone={summary.balance >= 0 ? "gold" : "red"} testid="stat-balance" />
        <StatCard label="Total income" value={usd(summary.total_income)} sub={`This month: ${usd(summary.mtd_income)}`} tone="green" testid="stat-income" />
        <StatCard label="Total expense" value={usd(summary.total_expense)} sub={`This month: ${usd(summary.mtd_expense)}`} tone="red" testid="stat-expense" />
        <StatCard label="Categories" value={String(cats.length)} sub={`${summary.top_categories.length} in use`} tone="default" testid="stat-cats" />
      </div>

      {summary.top_categories.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-4"><PieChart className="w-3.5 h-3.5" /> Top spend by category</div>
          <div className="space-y-3">
            {summary.top_categories.map((c) => {
              const pct = summary.total_expense ? (c.amount / summary.total_expense) * 100 : 0;
              const capPct = c.cap_amount ? Math.min((c.amount / c.cap_amount) * 100, 130) : null;
              return (
                <div key={c.category_id || "none"} data-testid={`cat-bar-${c.category_id || "none"}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: c.category_color }} /> <span className="text-zinc-200">{c.category_name}</span></span>
                    <span className="text-zinc-400">{usd(c.amount)} {c.cap_amount && <span className="text-zinc-600">· cap {usd(c.cap_amount)}</span>}</span>
                  </div>
                  <div className="h-1.5 bg-[#0A0A0A] rounded-full overflow-hidden">
                    <div className="h-full" style={{ width: `${capPct != null ? capPct : pct}%`, background: capPct && capPct >= 100 ? "#EF4444" : c.category_color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-3"><Filter className="w-3.5 h-3.5" /> Filter</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Select value={filters.type || "all"} onValueChange={(v) => setFilters({ ...filters, type: v === "all" ? "" : v })}>
            <SelectTrigger className="bg-[#121212] border-white/10 h-10"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
              <SelectItem value="income">Income</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.category_id || "all"} onValueChange={(v) => setFilters({ ...filters, category_id: v === "all" ? "" : v })}>
            <SelectTrigger className="bg-[#121212] border-white/10 h-10"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <DatePickerField testid="filter-start" value={filters.start_date} onChange={(v) => setFilters({ ...filters, start_date: v })} placeholder="From date" />
          <DatePickerField testid="filter-end" value={filters.end_date} onChange={(v) => setFilters({ ...filters, end_date: v })} placeholder="To date" />
          <button onClick={() => setFilters({ category_id: "", type: "", start_date: "", end_date: "" })} className="h-10 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5">Clear</button>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#121212] text-zinc-500 text-[10px] uppercase tracking-[0.2em]">
            <tr>
              <th className="text-left p-3 w-28">Date</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-right p-3">Balance</th>
              <th className="text-center p-3 w-24">Receipt</th>
              <th className="text-right p-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {txs.map((t) => (
              <tr key={t.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`tx-row-${t.id}`}>
                <td className="p-3 text-zinc-300 whitespace-nowrap">{fmtDate(t.date)}</td>
                <td className="p-3">
                  <div className="font-medium text-zinc-100">{t.description || t.payee || <span className="text-zinc-500">—</span>}</div>
                  {t.payee && t.description && <div className="text-[11px] text-zinc-500">to {t.payee}</div>}
                  <div className="text-[10px] text-zinc-600">by {t.entered_by_name}</div>
                </td>
                <td className="p-3">
                  {t.category_name ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-zinc-300"><span className="w-2 h-2 rounded-full" style={{ background: t.category_color }} /> {t.category_name}</span>
                  ) : <span className="text-zinc-600 text-xs">—</span>}
                </td>
                <td className={`p-3 text-right font-mono ${t.type === "income" ? "text-emerald-400" : "text-zinc-100"}`}>
                  <span className="inline-flex items-center gap-1">
                    {t.type === "income" ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5 text-red-400" />}
                    {usd(t.amount)}
                  </span>
                </td>
                <td className={`p-3 text-right font-mono ${runningTotals[t.id] >= 0 ? "text-zinc-200" : "text-red-400"}`}>{usd(runningTotals[t.id] || 0)}</td>
                <td className="p-3 text-center">
                  {t.receipt_url ? (
                    <a href={t.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-gold hover:underline text-xs"><Receipt className="w-3.5 h-3.5" /> View</a>
                  ) : <span className="text-zinc-600 text-xs">—</span>}
                </td>
                <td className="p-3 text-right">
                  {canEdit && (
                    <>
                      <button data-testid={`edit-tx-${t.id}`} onClick={() => setTxDialog({ mode: "edit", tx: t })} className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/5"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`del-tx-${t.id}`} onClick={() => del(t)} className="p-1.5 rounded text-zinc-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {txs.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-zinc-500">
                <Wallet className="w-8 h-8 mx-auto text-zinc-700 mb-2" />
                No transactions match. {canEdit && "Click Add entry to start."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {txDialog && (
        <TransactionDialog
          initial={txDialog.tx}
          cats={cats}
          productionId={productionId}
          onClose={() => setTxDialog(null)}
          onSaved={() => { setTxDialog(null); load(); }}
        />
      )}
      {catDialog && (
        <CategoriesDialog
          cats={cats}
          productionId={productionId}
          onClose={() => { setCatDialog(false); load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, sub, tone, testid }) {
  const ring = tone === "gold" ? "border-gold/40" : tone === "green" ? "border-emerald-500/30" : tone === "red" ? "border-red-500/30" : "border-white/10";
  return (
    <div data-testid={testid} className={`rounded-lg border ${ring} bg-[#0F0F0F] p-4`}>
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
      <div className="mt-2 font-display text-2xl md:text-3xl">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}

function TransactionDialog({ initial, cats, productionId, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    type: initial?.type || "expense",
    category_id: initial?.category_id || "",
    payee: initial?.payee || "",
    description: initial?.description || "",
    amount: initial?.amount || "",
  });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const editing = !!initial;

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a positive amount");
    if (!form.date) return toast.error("Pick a date");
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount), category_id: form.category_id || null };
      const res = editing
        ? await api.patch(`/budget/transactions/${initial.id}`, payload)
        : await api.post("/budget/transactions", payload, { params: { production_id: productionId } });
      // upload receipt if selected
      const file = fileRef.current?.files?.[0];
      if (file) {
        setUploading(true);
        const fd = new FormData(); fd.append("file", file);
        const token = localStorage.getItem("sc_token");
        await fetch(`${API_BASE}/budget/transactions/${res.data.id}/receipt`, {
          method: "POST", body: fd, credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      }
      toast.success(editing ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); setUploading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit entry" : "New entry"}</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="type-expense" onClick={() => setForm({ ...form, type: "expense" })} className={`h-10 rounded border text-sm font-medium inline-flex items-center justify-center gap-1.5 ${form.type === "expense" ? "border-red-500 bg-red-500/10 text-red-300" : "border-white/10 text-zinc-400"}`}><ArrowDownRight className="w-4 h-4" /> Expense</button>
            <button data-testid="type-income" onClick={() => setForm({ ...form, type: "income" })} className={`h-10 rounded border text-sm font-medium inline-flex items-center justify-center gap-1.5 ${form.type === "income" ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-white/10 text-zinc-400"}`}><ArrowUpRight className="w-4 h-4" /> Income</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-zinc-300 text-xs uppercase tracking-widest">Date</Label>
              <div className="mt-2"><DatePickerField testid="tx-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></div>
            </div>
            <div><Label className="text-zinc-300 text-xs uppercase tracking-widest">Amount (USD)</Label>
              <Input data-testid="tx-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 bg-[#121212] border-white/10 h-11" placeholder="0.00" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Category</Label>
            <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="tx-category" className="mt-2 bg-[#121212] border-white/10 h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="none">Uncategorized</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.id}><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: c.color }} /> {c.name}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">{form.type === "income" ? "From" : "Payee"}</Label>
            <Input data-testid="tx-payee" value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })} className="mt-2 bg-[#121212] border-white/10 h-11" placeholder={form.type === "income" ? "Ticket sales, donor…" : "Vendor / store"} />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Description</Label>
            <Textarea data-testid="tx-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 bg-[#121212] border-white/10" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Receipt (optional)</Label>
            <input data-testid="tx-receipt" ref={fileRef} type="file" accept="image/*,.pdf" className="mt-2 text-xs text-zinc-300 file:mr-3 file:h-9 file:px-3 file:rounded file:border-0 file:bg-white/10 file:text-zinc-200 hover:file:bg-white/15" />
            {initial?.receipt_url && (
              <div className="mt-1 text-[11px]"><a href={initial.receipt_url} target="_blank" rel="noreferrer" className="text-gold underline">View current receipt</a></div>
            )}
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="h-9 px-3 rounded border border-white/10 text-sm">Cancel</button>
          <button data-testid="save-tx-btn" onClick={submit} disabled={busy || uploading} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm">{busy ? "Saving…" : uploading ? "Uploading receipt…" : editing ? "Save" : "Add entry"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoriesDialog({ cats, productionId, onClose }) {
  const confirmCat = useConfirm();
  const [items, setItems] = useState(cats);
  const [form, setForm] = useState({ name: "", color: CAT_COLORS[0], cap_amount: "" });

  const refresh = async () => {
    const { data } = await api.get("/budget/categories", { params: { production_id: productionId } });
    setItems(data);
  };

  const add = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/budget/categories", {
        name: form.name, color: form.color,
        cap_amount: form.cap_amount ? Number(form.cap_amount) : null,
      }, { params: { production_id: productionId } });
      setForm({ name: "", color: CAT_COLORS[0], cap_amount: "" });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const update = async (c, patch) => {
    try {
      await api.patch(`/budget/categories/${c.id}`, {
        name: patch.name ?? c.name,
        color: patch.color ?? c.color,
        cap_amount: patch.cap_amount != null ? patch.cap_amount : c.cap_amount,
      });
      refresh();
    } catch { /* silent */ }
  };
  const del = async (c) => {
    const ok = await confirmCat({ title: `Delete "${c.name}"?`, body: "Existing entries stay but will no longer belong to this category.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    await api.delete(`/budget/categories/${c.id}`);
    refresh();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">Budget categories</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-3 grid grid-cols-1 md:grid-cols-12 gap-2">
            <Input data-testid="new-cat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Category name" className="md:col-span-5 bg-[#121212] border-white/10 h-10" />
            <div className="md:col-span-4 flex flex-wrap gap-1 items-center">
              {CAT_COLORS.map((c) => (
                <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-5 h-5 rounded-full ${form.color === c ? "ring-2 ring-white ring-offset-1 ring-offset-black" : ""}`} style={{ background: c }} data-testid={`cat-color-${c}`} />
              ))}
            </div>
            <Input data-testid="new-cat-cap" type="number" step="0.01" value={form.cap_amount} onChange={(e) => setForm({ ...form, cap_amount: e.target.value })} placeholder="Cap $" className="md:col-span-2 bg-[#121212] border-white/10 h-10" />
            <button data-testid="add-cat-btn" onClick={add} disabled={!form.name.trim()} style={goldBtn} className="md:col-span-1 h-10 rounded font-semibold text-sm">Add</button>
          </div>
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-white/5 first:border-t-0" data-testid={`cat-row-${c.id}`}>
                    <td className="p-3 w-8"><span className="w-3 h-3 inline-block rounded-full" style={{ background: c.color }} /></td>
                    <td className="p-3">
                      <input defaultValue={c.name} onBlur={(e) => e.target.value !== c.name && update(c, { name: e.target.value })} className="bg-transparent border-0 focus:outline-none focus:ring-0 text-zinc-100 w-full" />
                      <div className="text-[11px] text-zinc-500 mt-0.5">Spent {usd(c.spent)}{c.cap_amount ? ` of ${usd(c.cap_amount)}` : ""}</div>
                    </td>
                    <td className="p-3 text-right">
                      <input defaultValue={c.cap_amount ?? ""} type="number" step="0.01" placeholder="cap" onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== c.cap_amount) update(c, { cap_amount: v }); }} className="bg-[#121212] border border-white/10 rounded h-8 w-24 px-2 text-right text-xs" />
                    </td>
                    <td className="p-3 text-right w-10">
                      <button onClick={() => del(c)} className="p-1.5 rounded text-zinc-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-zinc-500 text-sm">No categories yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="h-9 px-4 rounded border border-white/10 text-sm">Done</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
